import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AssetDto } from '../../../../../../api/src/asset/models/asset.dto';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-photopea-iframe',
  templateUrl: './photopea-iframe.component.html',
  styleUrl: './photopea-iframe.component.scss'
})
export class PhotopeaIframeComponent implements OnInit, OnDestroy {
  @Input() asset: AssetDto;
  @Output() saveImage: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('photopeaFrame', { static: false }) photopeaFrame: ElementRef<HTMLIFrameElement>;

  public url: SafeResourceUrl;
  public isPhotopeaReady = false;
  public isSaving = false;
  private messageHandler: (event: MessageEvent) => void;

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    console.log('PhotopeaIframeComponent ngOnInit', this.asset);

    // Build Photopea configuration
    const config = {
      files: [this.asset.assetPath],
      environment: {
        lang: 'en'
      }
    };

    // Encode configuration and build URL
    const encodedConfig = encodeURIComponent(JSON.stringify(config));
    const unsafeUrl = `https://www.photopea.com#${encodedConfig}`;

    console.log('Photopea URL:', unsafeUrl);
    this.url = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);

    // Add event listener for postMessage from Photopea Live API
    this.messageHandler = this.handlePostMessage.bind(this);
    window.addEventListener('message', this.messageHandler, false);
  }

  ngOnDestroy(): void {
    // Remove event listener when the component is destroyed
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler, false);
    }
  }

  /**
   * Handle postMessage from Photopea
   * Photopea sends:
   * - "done" when initialized
   * - "done" after processing a message
   * - ArrayBuffer when saveToOE() is called
   */
  handlePostMessage(event: MessageEvent): void {
    // Ensure the message is from Photopea
    if (!event.origin.includes('photopea.com')) {
      return;
    }

    console.log('Photopea postMessage received:', event.data);

    // Check if Photopea is ready
    if (event.data === 'done' && !this.isPhotopeaReady) {
      this.isPhotopeaReady = true;
      console.log('Photopea is ready!');
      return;
    }

    // Handle ArrayBuffer response from saveToOE()
    if (event.data instanceof ArrayBuffer) {
      console.log('Received image data from Photopea, size:', event.data.byteLength);
      this.handleImageData(event.data);
    }
  }

  /**
   * Trigger save from Photopea by sending script to export image
   * This is called when user clicks "Done" button
   */
  public triggerSave(): void {
    if (!this.isPhotopeaReady || !this.photopeaFrame || this.isSaving) {
      console.warn('Photopea is not ready yet or already saving');
      return;
    }

    this.isSaving = true;
    console.log('Triggering save from Photopea...');

    // Send script to Photopea to export the current document
    // saveToOE("png") sends the image as PNG ArrayBuffer via postMessage
    const script = 'app.activeDocument.saveToOE("png");';
    const iframe = this.photopeaFrame.nativeElement;

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(script, '*');
    }
  }

  /**
   * Handle the image data received from Photopea
   * Convert ArrayBuffer to Blob and upload to S3
   */
  private async handleImageData(arrayBuffer: ArrayBuffer): Promise<void> {
    try {
      // Convert ArrayBuffer to Blob
      const blob = new Blob([arrayBuffer], { type: 'image/png' });

      // Create a File object from the Blob
      const fileName = `photopea-edit-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      console.log('Created file from Photopea data:', fileName, file.size, 'bytes');

      // Upload to your server/S3
      // For now, create a local blob URL for immediate preview
      const blobUrl = URL.createObjectURL(blob);

      // TODO: Upload to S3 and get permanent URL
      // For now, emit the blob URL
      // In production, you'd upload to S3 first, then emit the S3 URL
      this.saveImage.emit(blobUrl);

      console.log('Emitted saved image URL:', blobUrl);
    } catch (error) {
      console.error('Error handling Photopea image data:', error);
    }
  }
}
